/* eslint-disable max-len */
import React, { useState, useEffect } from 'react';
import {
  CForm, CRow, CCol, CFormLabel, CFormSelect, CButton,
  CSpinner, CAlert
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilSave, cilX } from '@coreui/icons';
import PropTypes from 'prop-types';

const WeaveColorInputs = ({
  onSubmit, onCancel, isLoading = false,
  submitResponse = null, initialData = null, isEdit = false,
  weaveList = [], threadList = [], yarnList = [], colorList = []
}) => {
  const [formData, setFormData] = useState({
    prdWeavesGuid: '',
    prdThreadGuid: '',
    prdYarnGuid: '',
    prdColorGuid: ''
  });

  const [errors, setErrors] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (initialData) {
      setFormData({
        prdWeavesGuid: initialData.PrdWeavesGuid || initialData.WeavesGuid || '',
        prdThreadGuid: initialData.PrdThreadGuid || initialData.ThreadGuid || '',
        prdYarnGuid: initialData.PrdYarnGuid || initialData.YarnGuid || '',
        prdColorGuid: initialData.PrdColorGuid || initialData.ColorGuid || ''
      });
    }
  }, [initialData]);

  useEffect(() => {
    if (submitResponse?.Result?.ErrorCode === 0) {
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2000);
      if (!isEdit) {
        setFormData({
          prdWeavesGuid: '',
          prdThreadGuid: '',
          prdYarnGuid: '',
          prdColorGuid: ''
        });
      }
    }
  }, [submitResponse, isEdit]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.prdWeavesGuid) newErrors.prdWeavesGuid = 'Weave is required.';
    if (!formData.prdThreadGuid) newErrors.prdThreadGuid = 'Thread is required.';
    if (!formData.prdYarnGuid) newErrors.prdYarnGuid = 'Yarn is required.';
    if (!formData.prdColorGuid) newErrors.prdColorGuid = 'Color is required.';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const sessionUserId = localStorage.getItem('sessionUserId');
    if (!sessionUserId) {
      setErrors({ general: 'Session user not found.' });
      return;
    }

    const payload = {
      SessionUserId: sessionUserId,
      PrdWeavesGuid: formData.prdWeavesGuid,
      PrdThreadGuid: formData.prdThreadGuid,
      PrdYarnGuid: formData.prdYarnGuid,
      PrdColorGuid: formData.prdColorGuid
    };

    if (isEdit && (initialData?.WeaveColorId || initialData?.WeaveColorID || initialData?.Id)) {
      payload.WeaveColorId = initialData.WeaveColorId || initialData.WeaveColorID || initialData.Id;
    }

    onSubmit(payload);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  return (
    <div>
      {showSuccess && <CAlert color="success">Saved successfully!</CAlert>}
      {errors.general && <CAlert color="danger">{errors.general}</CAlert>}
      {submitResponse?.Result?.ErrorCode !== 0 && (
        <CAlert color="danger">{submitResponse?.Result?.ErrorDescription}</CAlert>
      )}

      <CForm onSubmit={handleSubmit}>
        <CRow className="mb-3">
          <CCol md={6}>
            <CFormLabel htmlFor="prdWeavesGuid">Weave *</CFormLabel>
            <CFormSelect
              id="prdWeavesGuid"
              name="prdWeavesGuid"
              value={formData.prdWeavesGuid}
              onChange={handleChange}
              disabled={isLoading}
              invalid={!!errors.prdWeavesGuid}
            >
              <option value="">Select Weave</option>
              {weaveList.map(w => (
                <option key={w.Guid} value={w.Guid}>
                  {w.WeaveCode}
                </option>
              ))}
            </CFormSelect>
            {errors.prdWeavesGuid && <div className="invalid-feedback d-block">{errors.prdWeavesGuid}</div>}
          </CCol>

          <CCol md={6}>
            <CFormLabel htmlFor="prdThreadGuid">Thread *</CFormLabel>
            <CFormSelect
              id="prdThreadGuid"
              name="prdThreadGuid"
              value={formData.prdThreadGuid}
              onChange={handleChange}
              disabled={isLoading}
              invalid={!!errors.prdThreadGuid}
            >
              <option value="">Select Thread</option>
              {threadList.map(t => (
                <option key={t.Guid} value={t.Guid}>
                  {t.ThreadName}
                </option>
              ))}
            </CFormSelect>
            {errors.prdThreadGuid && <div className="invalid-feedback d-block">{errors.prdThreadGuid}</div>}
          </CCol>
        </CRow>

        <CRow className="mb-3">
          <CCol md={6}>
            <CFormLabel htmlFor="prdYarnGuid">Yarn *</CFormLabel>
            <CFormSelect
              id="prdYarnGuid"
              name="prdYarnGuid"
              value={formData.prdYarnGuid}
              onChange={handleChange}
              disabled={isLoading}
              invalid={!!errors.prdYarnGuid}
            >
              <option value="">Select Yarn</option>
              {yarnList.map(y => (
                <option key={y.Guid} value={y.Guid}>
                  {y.YarnName}
                </option>
              ))}
            </CFormSelect>
            {errors.prdYarnGuid && <div className="invalid-feedback d-block">{errors.prdYarnGuid}</div>}
          </CCol>

          <CCol md={6}>
            <CFormLabel htmlFor="prdColorGuid">Color *</CFormLabel>
            <CFormSelect
              id="prdColorGuid"
              name="prdColorGuid"
              value={formData.prdColorGuid}
              onChange={handleChange}
              disabled={isLoading}
              invalid={!!errors.prdColorGuid}
            >
              <option value="">Select Color</option>
              {colorList.map(c => (
                <option key={c.Guid} value={c.Guid}>
                  {c.ColorName}
                </option>
              ))}
            </CFormSelect>
            {errors.prdColorGuid && <div className="invalid-feedback d-block">{errors.prdColorGuid}</div>}
          </CCol>
        </CRow>

        <CRow>
          <CCol className="d-flex gap-2">
            <CButton type="submit" color="primary" disabled={isLoading}>
              {isLoading ? (
                <>
                  <CSpinner size="sm" className="me-2" />
                  Saving...
                </>
              ) : (
                <>
                  <CIcon icon={cilSave} className="me-2" />
                  Save
                </>
              )}
            </CButton>
            <CButton color="secondary" variant="outline" onClick={onCancel} disabled={isLoading}>
              <CIcon icon={cilX} className="me-2" />
              Cancel
            </CButton>
          </CCol>
        </CRow>
      </CForm>
    </div>
  );
};

WeaveColorInputs.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  submitResponse: PropTypes.object,
  initialData: PropTypes.object,
  isEdit: PropTypes.bool,
  weaveList: PropTypes.array,
  threadList: PropTypes.array,
  yarnList: PropTypes.array,
  colorList: PropTypes.array,
};

export default WeaveColorInputs;
